<template>
  <ds-dialog
    id="stock-match-comparison"
    size="xl"
    :title="i18n.t('/stockmatch/comparison/title')"
    :subtitle="subtitle"
    @close="$emit('close')"
  >
    <div class="stock-match-comparison__grid">
      <section class="stock-match-comparison__car">
        <p class="stock-match-comparison__label">{{ i18n.t('/stockmatch/referencetitle') }}</p>
        <img v-if="configImages.exterior" :src="configImages.exterior" :alt="configTitle" />
        <div v-else class="stock-match-comparison__no-photo"></div>
        <p class="stock-match-comparison__model">{{ configTitle }}</p>
        <stock-match-price :price="configPrice" />
        <div class="stock-match-comparison__origin">
          <div>
            <p class="stock-match-comparison__strong">
              {{ i18n.t('/stockmatch/comparison/quoteconfiguration') }}
            </p>
            <p>
              {{ i18n.t('/stockmatch/comparison/configurationcode', { code: configurationCode }) }}
            </p>
          </div>
        </div>
        <stock-match-score reference :rows="referenceRows" />
      </section>
      <section class="stock-match-comparison__car">
        <p class="stock-match-comparison__label">
          {{ i18n.t('/stockmatch/comparison/stockcarlabel') }}
        </p>
        <img v-if="stockImages.Exterior" :src="stockImages.Exterior" :alt="stockTitle" />
        <div v-else class="stock-match-comparison__no-photo"></div>
        <p class="stock-match-comparison__model">{{ stockTitle }}</p>
        <stock-match-price :price="stockPrice" :difference="stockPrice - configPrice" />
        <div class="stock-match-comparison__origin">
          <div>
            <p class="stock-match-comparison__strong">{{ origin }}</p>
            <p>{{ i18n.t('/stockmatch/komnumberlabel') }} {{ komNumber }}</p>
          </div>
          <span v-if="status" class="ds-chip ds-chip--neutral ds-chip--dot">{{ status }}</span>
        </div>
        <stock-match-score :rows="rows" :extras="extras" />
      </section>

      <div class="stock-match-comparison__row-label">
        {{ i18n.t('/stockmatch/comparison/engine') }}
      </div>
      <stock-match-row class="stock-match-comparison__cell" :name="engine" />
      <stock-match-row
        class="stock-match-comparison__cell stock-match-comparison__cell--unmarked"
        :name="engine"
      />

      <template v-for="(reference, index) in referenceRows" :key="reference.code + index">
        <div class="stock-match-comparison__row-label">{{ rowLabel(reference, index) }}</div>
        <stock-match-row
          class="stock-match-comparison__cell"
          :code="reference.code"
          :name="reference.name"
        />
        <stock-match-row
          class="stock-match-comparison__cell"
          :code="rows[index]?.code"
          :name="rows[index]?.name"
          :matches="rows[index]?.matches"
        />
      </template>

      <template v-for="(extra, index) in extras" :key="extra.code">
        <div class="stock-match-comparison__row-label">
          {{ index === 0 ? i18n.t('/stockmatch/comparison/extra') : '' }}
        </div>
        <div class="stock-match-comparison__cell stock-match-comparison__cell--muted">
          {{ index === 0 ? i18n.t('/stockmatch/comparison/notinconfiguration') : '' }}
        </div>
        <stock-match-row
          class="stock-match-comparison__cell"
          :code="extra.code"
          :name="extra.name"
          extra
        />
      </template>

      <template v-for="photo in photos" :key="photo.label">
        <div class="stock-match-comparison__row-label stock-match-comparison__photo">
          {{ photo.label }}
        </div>
        <div v-for="(src, side) in photo.sources" :key="side" class="stock-match-comparison__photo">
          <img v-if="src" :src="src" alt="" loading="lazy" />
          <div v-else class="stock-match-comparison__no-photo"></div>
        </div>
      </template>
    </div>

    <template #footer>
      <ds-button
        variant="ghost"
        data-qa="stock-match-comparison-reservation-status-button"
        @click="$emit('reservation-status')"
      >
        {{ i18n.t('/stockmatch/reservationstatusbutton') }}
      </ds-button>
      <ds-button data-qa="stock-match-comparison-quote-button" @click="$emit('stock-quote')">
        {{ i18n.t('/stockmatch/comparison/quotebtn') }}
      </ds-button>
    </template>
  </ds-dialog>
</template>

<script>
import DsDialog from '@/components/ds/molecules/dsDialog/DsDialog.vue'
import DsButton from '@/components/ds/atoms/dsButton/DsButton.vue'
import StockMatchPrice from '@/components/generic/presentational/molecules/stockMatchPrice/StockMatchPrice.vue'
import StockMatchRow from '@/components/generic/presentational/molecules/stockMatchRow/StockMatchRow.vue'
import StockMatchScore from '@/components/generic/presentational/molecules/stockMatchScore/StockMatchScore.vue'

export default {
  components: { DsDialog, DsButton, StockMatchPrice, StockMatchRow, StockMatchScore },
  props: {
    configTitle: { type: String, default: '' },
    configPrice: { type: Number, required: true },
    configurationCode: { type: String, default: '' },
    configImages: { type: Object, default: () => ({}) },
    engine: { type: String, default: '' },
    referenceRows: { type: Array, default: () => [] },
    stockTitle: { type: String, default: '' },
    stockPrice: { type: Number, required: true },
    origin: { type: String, default: '' },
    komNumber: { type: String, default: '' },
    status: { type: String, default: '' },
    stockImages: { type: Object, default: () => ({}) },
    rows: { type: Array, default: () => [] },
    extras: { type: Array, default: () => [] },
  },
  emits: ['close', 'stock-quote', 'reservation-status'],
  data() {
    return {
      i18n: window.VUE_CLI_EXPORTS.plugins.i18n,
    }
  },
  computed: {
    subtitle() {
      return [
        this.stockTitle,
        this.origin,
        `${this.i18n.t('/stockmatch/komnumberlabel')} ${this.komNumber}`,
      ].join(' · ')
    },
    photos() {
      return [
        {
          label: this.i18n.t('/stockmatch/comparison/rear'),
          sources: [this.configImages.extRear, this.stockImages.ExtRear],
        },
        {
          label: this.i18n.t('/stockmatch/comparison/interiorside'),
          sources: [this.configImages.intSide, this.stockImages.IntSide],
        },
        {
          label: this.i18n.t('/stockmatch/comparison/interiorfront'),
          sources: [this.configImages.interior, this.stockImages.Interior],
        },
      ].filter((photo) => photo.sources.some(Boolean))
    },
  },
  methods: {
    rowLabel(row, index) {
      if (row.kind === 'color') return this.i18n.t('/stockmatch/comparison/color')
      if (row.kind === 'interior') return this.i18n.t('/stockmatch/comparison/interior')
      const isFirstOption = this.referenceRows[index - 1]?.kind !== 'option'
      return isFirstOption ? this.i18n.t('/stockmatch/options-header') : ''
    },
  },
}
</script>

<style lang="scss" scoped>
.stock-match-comparison__grid {
  display: grid;
  grid-template-columns: 200px minmax(0, 1fr) minmax(0, 1fr);
  column-gap: var(--ds-space-6);
  color: var(--ds-color-text);

  img,
  .stock-match-comparison__no-photo {
    display: block;
    width: 100%;
    aspect-ratio: 8 / 5;
    object-fit: contain;
    border-radius: var(--ds-radius-md);
    background: var(--ds-color-surface-sunken);
  }
}

.stock-match-comparison__car {
  display: flex;
  flex-direction: column;
  padding-bottom: var(--ds-space-5);

  &:first-child {
    grid-column: 2;
  }
}

.stock-match-comparison__label,
.stock-match-comparison__row-label {
  font-size: var(--ds-font-size-xs);
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: var(--ds-color-text-muted);
}

.stock-match-comparison__car .stock-match-comparison__label {
  margin-bottom: var(--ds-space-3);
}

.stock-match-comparison__model {
  margin-top: var(--ds-space-4);
  font-weight: 700;
}

.stock-match-comparison__origin {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: var(--ds-space-3);
  margin: var(--ds-space-4) 0;
  padding-top: var(--ds-space-4);
  border-top: 1px solid var(--ds-color-border-subtle);
  font-size: var(--ds-font-size-sm);
  color: var(--ds-color-text-muted);
}

.stock-match-comparison__strong {
  font-weight: 700;
  color: var(--ds-color-text);
}

.stock-match-comparison__row-label,
.stock-match-comparison__cell {
  display: flex;
  align-items: center;
  min-height: 2.75rem;
  border-top: 1px solid var(--ds-color-border-subtle);
}

.stock-match-comparison__cell--muted {
  color: var(--ds-color-text-subtle);
}

// Lines the engine up with the names below it, which sit after a mark.
.stock-match-comparison__cell--unmarked {
  padding-left: calc(18px + var(--ds-space-2));
}

.stock-match-comparison__photo {
  padding: var(--ds-space-4) 0;
}
</style>
